import { z } from 'zod';
// Gaps: what an agent couldn't answer (knowledge) or couldn't do (action), grouped into topics by the gap pipeline.
// The app returns one row per occurrence with its group's details; Via reads them grouped, most frequent first.
const MAX_EXAMPLES = 3;
const gapStatusSchema = z.enum(['active', 'resolved', 'archived']);
export const listGapsToolSchema = {
    name: 'list_gaps',
    description: "List an agent's gaps, most frequent first: knowledge gaps (questions it couldn't answer) and action gaps (requests it couldn't act on), each with how often it happened, a suggested fix, and example conversations.",
    inputSchema: z.object({
        agentId: z.string().optional().describe("From list_agents; every agent's gaps when left out"),
        status: gapStatusSchema
            .optional()
            .describe('Defaults to active; resolved and archived gaps are hidden on the page')
    }),
    outputSchema: z.object({
        gaps: z.array(z.object({
            gapId: z.string(),
            agentId: z.string(),
            kind: z.enum(['knowledge', 'action']),
            topic: z.string(),
            description: z.string(),
            count: z.number(),
            lastSeenAt: z.string(),
            status: gapStatusSchema,
            suggestion: z.string().optional().describe('What the gap pipeline suggests adding'),
            examples: z.array(z.object({
                conversationId: z.string(),
                kind: z.enum(['call', 'chat']),
                what: z.string().describe('What the customer asked for')
            }))
        }))
    })
};
export const setGapStatusToolSchema = {
    name: 'set_gap_status',
    description: 'Mark a gap resolved once the agent can handle it, archive it if it needs no fix, or make it active again.',
    inputSchema: z.object({
        gapId: z.string().describe('From list_gaps'),
        status: gapStatusSchema
    }),
    outputSchema: z.object({ gapId: z.string(), status: gapStatusSchema })
};
const describeSuggestion = (metadata) => {
    if (!metadata)
        return undefined;
    if ('capability' in metadata && metadata.capability) {
        return metadata.suggestedToolType
            ? `${metadata.capability} (${metadata.suggestedToolType})`
            : metadata.capability;
    }
    if ('steps' in metadata && metadata.steps?.length) {
        return metadata.steps
            .map(({ fileName, question, answer }) => `Add to ${fileName}: "${question}" answered "${answer}"`)
            .join('\n');
    }
    return undefined;
};
/** Groups gap occurrences for list_gaps. */
export const groupGaps = (occurrences, { agentId, status = 'active' }) => {
    const groups = new Map();
    for (const occurrence of occurrences) {
        if ((agentId && occurrence.agentId !== agentId) || occurrence.clusterStatus !== status)
            continue;
        groups.set(occurrence.clusterId, [...(groups.get(occurrence.clusterId) ?? []), occurrence]);
    }
    return [...groups.values()]
        .flatMap((group) => {
        const [first] = group;
        if (!first)
            return [];
        const newestFirst = [...group].sort((a, b) => b.createdAt - a.createdAt);
        return [
            {
                gapId: first.clusterId,
                agentId: first.agentId,
                kind: first.gapType,
                topic: first.clusterCategory,
                description: first.clusterDescription,
                count: group.length,
                lastSeenAt: new Date(newestFirst[0]?.createdAt ?? first.createdAt).toISOString(),
                status: first.clusterStatus,
                suggestion: describeSuggestion(first.clusterMetadata),
                examples: newestFirst
                    .slice(0, MAX_EXAMPLES)
                    .flatMap(({ callId, chatId, description }) => {
                    if (callId)
                        return [{ conversationId: callId, kind: 'call', what: description }];
                    if (chatId)
                        return [{ conversationId: chatId, kind: 'chat', what: description }];
                    return [];
                })
            }
        ];
    })
        .sort((a, b) => b.count - a.count);
};
//# sourceMappingURL=gaps.js.map